import React, { useRef, useCallback, useEffect, useState } from 'react';
import download from 'downloadjs';
import { styled } from 'react-free-style';
import { notify } from 'react-notify-toast';

import { Icons, colors } from '@united-talent-agency/components';
import { requestPromise } from '@united-talent-agency/julius-frontend-store';
import { gql, useMutation } from '@apollo/client';
import { useLogoutMutation } from '../../../generated/graphql';
import { getStoredGraphQLToken } from '../../../utils/getStoredGraphQLToken';

const SEND_PHONESHEET_EMAIL = gql`
  mutation SendPhonesheetEmail($input: SendPhonesheetEmailInput!) {
    sendPhonesheetEmail(input: $input) {
      success
      message
      emailId
    }
  }
`;

import ArchiveSearch from '../ArchiveSearch';
import {
  createShortcuts,
  filterInputEvent,
  keyboard,
  stringifyKey,
} from '../../../support/keyboard';
import { apiServerUrl } from '../../../support/urls';
import HeaderMenu from '../../../components/HeaderMenu';
import SettingsModalContent from '../../../components/SettingsModalContent';
import SearchBar from '../../../components/SearchBar';
import ReactModal from 'react-modal';
import PrintSettingsModal from '../../../components/PrintSettingsModal';
import { buildPrintQuery } from '../../../utils/build-print-query';
import {
  useUserPhoneSheetPrintSettingsQuery,
  useUpdateUserPhoneSheetPrintSettingsMutation,
} from '../../../generated/graphql';

const Component = ({
  user,
  desk,
  styles,
  search,
  onCallAdd,
  totalCounts,
  updateQuery,
  deskStatuses,
}) => {
  // Refs
  const searchInput = useRef();

  // States
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isCsvFileDownloading, setIsCsvFileDownloading] = useState(false);
  const [isSendingPhonesheetEmail, setIsSendingPhonesheetEmail] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isPrintSettingsOpen, setIsPrintSettingsOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // GraphQL Mutations
  const [logoutMutation] = useLogoutMutation();

  const [sendPhonesheetEmailMutation] = useMutation(SEND_PHONESHEET_EMAIL);

  // Per-user, per-desk PDF print settings (FILM-385)
  const { data: printSettingsData, loading: printSettingsLoading } =
    useUserPhoneSheetPrintSettingsQuery({
      variables: { id: user?._id },
      skip: !user?._id,
    });
  const [updatePrintSettings] = useUpdateUserPhoneSheetPrintSettingsMutation();
  // Locally remember what was just saved per desk, so reopening the dialog immediately
  // reflects the latest choice regardless of the async refetch / cache timing.
  const [savedPrintSettingsByDesk, setSavedPrintSettingsByDesk] = useState({});
  const queryPrintSettings =
    printSettingsData?.users?.[0]?.phoneSheetPrintSettings?.find(
      (s) => String(s.deskId) === String(desk?._id)
    ) || null;
  const hasLocalPrintSettings = Object.prototype.hasOwnProperty.call(
    savedPrintSettingsByDesk,
    desk?._id
  );
  const currentPrintSettings = hasLocalPrintSettings
    ? savedPrintSettingsByDesk[desk?._id]
    : queryPrintSettings;
  // Only "loading" until we have an answer — a local save short-circuits the wait.
  // deskStatuses is undefined (not []) until it loads; without this the dialog would render
  // an empty status list while silently still applying the saved exclusions on Print.
  const printSettingsPending = (printSettingsLoading && !hasLocalPrintSettings) || !deskStatuses;

  useEffect(() => {
    const keyboardListener = filterInputEvent(
      createShortcuts({
        [stringifyKey('/')]: (e) => {
          e.preventDefault();

          return searchInput.current?.focus && searchInput.current?.focus();
        },
      })
    );

    keyboard.addListener(keyboardListener);

    return () => {
      keyboard.removeListener(keyboardListener);
    };
  }, []);

  const closeSettings = () => {
    setIsSettingsModalOpen(false);
  };

  const handleOnSaveSettings = () => {
    closeSettings();
  };

  const openSettings = () => {
    setIsSettingsModalOpen(true);
  };

  const toggleArchive = useCallback(() => {
    setIsArchiveModalOpen((oldState) => !oldState);
  }, []);

  // TODO: just, no
  // send request to backend and let it handle
  // the querying there, not in frontend
  const requestFile = useCallback(
    async (contentType, fileName, params) => {
      const fileRequest = await requestPromise(
        user,
        apiServerUrl,
        '/call_todo',
        'GET',
        null,
        params ?? [
          { $populate: 'recipientId' },
          { $populate: 'companyId' },
          { $populate: 'deskId' },
          { '$sort[occurrence_date]': '-1' },
          { $excludes: 'personId.profile_pic' },
          // Benign param, translated server-side into the status filter. The raw Mongo
          // operator (status[$ne]) gets blocked by the Cloudflare WAF as NoSQL injection,
          // and the block page bytes were being saved as a corrupted pdf/csv.
          { excludeStatuses: 'AR' },
          { $limit: '500' },
          { timezone: new Date().getTimezoneOffset() },
          { deskId: desk?._id },
        ],
        contentType
      );
      // requestPromise returns the raw fetch Response for pdf/csv, and fetch resolves on
      // HTTP errors — without this check a 404 body gets saved as the "file" and the
      // caller reports success.
      if (!fileRequest.ok) {
        throw new Error(`Download failed: the server responded with ${fileRequest.status}`);
      }
      const responseBlob = await fileRequest.blob();
      download(responseBlob, fileName, contentType);
    },
    [desk?._id, user]
  );

  // PDF now goes through the print settings dialog (handlePrintWithSettings); this only
  // handles the CSV export.
  const downloadCsvFile = useCallback(async () => {
    setIsCsvFileDownloading(true);
    try {
      await requestFile('text/csv', 'phone-sheet.csv');
    } catch (error) {
      console.error('Failed to download CSV:', error);
      notify.show(error.message || 'Failed to download CSV', 'error');
    } finally {
      setIsCsvFileDownloading(false);
    }
  }, [requestFile]);

  const downloadPdf = useCallback(() => {
    // Open the print settings dialog; the actual download happens on confirm (FILM-385).
    setIsPrintSettingsOpen(true);
  }, []);

  const handlePrintWithSettings = useCallback(
    async (settings) => {
      if (isPrinting) {
        return;
      }
      setIsPrinting(true);

      // Persisting the settings and producing the PDF are independent: a failed save must not
      // cost the user their print. So the save is kicked off here, but only its failure (not
      // its timing) is allowed to change the outcome. Refetch keeps the server-backed query in
      // sync for other sessions/devices (Apollo runs with addTypename: false, so the mutation
      // result isn't auto-merged into the query).
      let saveFailed = false;
      const save = updatePrintSettings({
        variables: { input: { phoneSheetPrintSettings: [{ deskId: desk?._id, ...settings }] } },
        refetchQueries: ['UserPhoneSheetPrintSettings'],
      }).catch((error) => {
        saveFailed = true;
        console.error('Failed to save print settings:', error);
      });

      try {
        const params = buildPrintQuery({ deskId: desk?._id, settings });
        await requestFile('application/pdf', 'phone-sheet.pdf', params);
        await save;

        if (!saveFailed) {
          // Remember the choice locally so a quick reopen reflects it immediately, independent
          // of the async refetch/cache timing.
          setSavedPrintSettingsByDesk((prev) => ({ ...prev, [desk?._id]: settings }));
        }

        setIsPrintSettingsOpen(false);
        if (saveFailed) {
          notify.show('Phonesheet printed, but the settings could not be saved', 'warning');
        } else {
          notify.show('Printed successfully', 'success');
        }
      } catch (error) {
        // The print itself failed — keep the dialog open so the choice isn't lost.
        console.error('Failed to print phonesheet:', error);
        notify.show(error.message || 'Failed to print phonesheet', 'error');
      } finally {
        setIsPrinting(false);
      }
    },
    [desk?._id, isPrinting, requestFile, updatePrintSettings]
  );

  const downloadCsv = useCallback(() => {
    if (!isCsvFileDownloading) {
      downloadCsvFile();
    }
  }, [downloadCsvFile, isCsvFileDownloading]);

  const requestEmail = useCallback(async () => {
    try {
      const { data } = await sendPhonesheetEmailMutation({
        variables: {
          input: {
            deskId: desk?._id,
            timezone: new Date().getTimezoneOffset(),
          },
        },
      });
      return data.sendPhonesheetEmail;
    } catch (error) {
      console.error('Error sending phonesheet email:', error);
      return { success: false, message: error.message };
    }
  }, [desk?._id, sendPhonesheetEmailMutation]);

  const sendEmail = useCallback(async () => {
    setIsSendingPhonesheetEmail(true);
    const data = await requestEmail();
    if (data.success) {
      notify.show('Email Sent!', 'success');
    } else {
      notify.show('Email failed to send...', 'error');
    }
    setIsSendingPhonesheetEmail(false);
  }, [requestEmail]);

  const emailPhonesheet = useCallback(() => {
    if (!isSendingPhonesheetEmail) {
      sendEmail();
    }
  }, [isSendingPhonesheetEmail, sendEmail]);

  const signOut = useCallback(async () => {
    try {
      // Call GraphQL logout mutation first
      const userLoginToken = getStoredGraphQLToken();
      if (userLoginToken) {
        await logoutMutation({
          variables: {
            email: user.email,
            currentToken: userLoginToken,
            loginInterface: 'WEB',
          },
        });
        console.log('GraphQL logout successful');
      }
    } catch (error) {
      console.warn('GraphQL logout failed:', error);
      // Continue with local logout even if GraphQL logout fails
    }

    // Clear local storage and redirect
    localStorage.removeItem('uta.auth');
    // TODO: Tracking
    // Logout user
    // userId: null,
    // userName: null,
    // userEmail: null,
    window.location = '/login';
  }, [logoutMutation, user]);

  const menuOptions = [
    { name: 'Print Phonesheet (PDF)', MenuOptionIcon: Icons['PrinterIcon'], onSelect: downloadPdf },
    {
      name: 'Download Phonesheet (CSV)',
      MenuOptionIcon: Icons['TableIcon'],
      onSelect: downloadCsv,
    },
    { name: 'Email Phonesheet', MenuOptionIcon: Icons['EmailIcon'], onSelect: emailPhonesheet },
    {
      name: 'DialLog Data Archive',
      MenuOptionIcon: Icons['PhoneOutgoingIcon'],
      onSelect: toggleArchive,
    },
    { name: 'Settings', MenuOptionIcon: Icons['SettingsIcon'], onSelect: openSettings },
    {
      name: 'Sign Out',
      MenuOptionIcon: Icons['LogOutIcon'],
      props: { hFlip: true },
      onSelect: signOut,
    },
  ];

  return (
    <div className={styles.headerContainer}>
      <div className={styles.searchColumn}>
        <SearchBar ref={searchInput} styles={styles} search={search} updateQuery={updateQuery} />
        <div className={styles.menuColumn}>
          <HeaderMenu menuOptions={menuOptions} MenuMainIcon={Icons['HamburgerMenuIcon']} />
        </div>
      </div>
      <ReactModal
        isOpen={isSettingsModalOpen}
        onRequestClose={closeSettings}
        style={{
          content: {
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            border: '0.5px solid rgba(0, 0, 0, 0.2)',
            background: `${colors.background} none repeat scroll 0% 0%`,
            borderRadius: 2,
            outline: 'currentcolor none medium',
            width: 454,
            overflow: 'unset',
            height: 'min-content',
            padding: '20px 0',
          },
        }}
      >
        <SettingsModalContent
          desk={desk}
          totalCounts={totalCounts}
          deskStatuses={deskStatuses}
          onSave={handleOnSaveSettings}
          onCancel={closeSettings}
        />
      </ReactModal>
      {/* Always render when open (so the action gives immediate feedback). While settings
          are still loading the dialog shows a loading state with Print disabled; the `key`
          forces a fresh mount once loaded so state initializes from the real saved values. */}
      {isPrintSettingsOpen && (
        <PrintSettingsModal
          key={printSettingsPending ? 'print-settings-loading' : 'print-settings-ready'}
          isOpen
          loading={printSettingsPending}
          printing={isPrinting}
          deskStatuses={deskStatuses}
          settings={currentPrintSettings}
          defaultHideFutureCalls={!!desk?.settings?.hideFutureCalls}
          onPrint={handlePrintWithSettings}
          onCancel={() => !isPrinting && setIsPrintSettingsOpen(false)}
        />
      )}
      <ArchiveSearch
        user={user}
        isOpen={isArchiveModalOpen}
        closeModal={toggleArchive}
        onPersonSelected={onCallAdd}
      />
    </div>
  );
};

const withStyles = styled({
  headerContainer: {
    background: colors.white,
    display: 'flex',
    flexDirection: 'row',
  },
  search: {
    width: '100%',
  },
  searchContainer: {
    margin: '10px',
  },
  searchInput: {
    '&&': {
      border: `1px solid ${colors.border}`,
      background: colors.white,
    },
  },
  multiselect: {
    display: 'flex',
    marginTop: 25,
    flex: 0.2,
    marginLeft: '10px',
    alignItems: 'center',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    alignItems: 'flex-start',
    width: 200,
  },
  searchColumn: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-end',
    alignItems: 'flex-end',
    margin: '10px 5px',
    justifyContent: 'center',
  },
});

const Header = withStyles(Component);

export default Header;
